import { lazy, Suspense } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { useAuthStore } from './stores/auth.store'
import AdminLayout from './layouts/AdminLayout'

const LoginPage = lazy(() => import('./pages/LoginPage'))
const DashboardPage = lazy(() => import('./pages/DashboardPage'))
const WorkspacesPage = lazy(() => import('./pages/WorkspacesPage'))
const WorkspaceDetailPage = lazy(() => import('./pages/WorkspaceDetailPage'))
const UsersPage = lazy(() => import('./pages/UsersPage'))
const UserDetailPage = lazy(() => import('./pages/UserDetailPage'))
const ChannelsPage = lazy(() => import('./pages/ChannelsPage'))
const AdminsPage = lazy(() => import('./pages/AdminsPage'))
const AuditLogPage = lazy(() => import('./pages/AuditLogPage'))
const SettingsPage = lazy(() => import('./pages/SettingsPage'))
const BillingPage = lazy(() => import('./pages/BillingPage'))
const CompliancePage = lazy(() => import('./pages/CompliancePage'))
const BrandingPage = lazy(() => import('./pages/BrandingPage'))
const AnalyticsPage = lazy(() => import('./pages/AnalyticsPage'))
const PlatformAnalyticsPage = lazy(
  () => import('./pages/PlatformAnalyticsPage')
)
const WorkflowRuleManagerPage = lazy(
  () => import('./pages/WorkflowRuleManagerPage')
)
const WebhookInspectionPage = lazy(
  () => import('./pages/WebhookInspectionPage')
)
const ContactBrowserPage = lazy(() => import('./pages/ContactBrowserPage'))
const PhonePoolsPage = lazy(() => import('./pages/PhonePoolsPage'))
const PricingPlansPage = lazy(() => import('./pages/PricingPlansPage'))
const FeatureManagementPage = lazy(
  () => import('./pages/FeatureManagementPage')
)
const SecurityDashboardPage = lazy(
  () => import('./pages/SecurityDashboardPage')
)
const AIConfigPage = lazy(() => import('./pages/AIConfigPage'))
const QueueManagementPage = lazy(() => import('./pages/QueueManagementPage'))
const AICostAnalyticsPage = lazy(() => import('./pages/AICostAnalyticsPage'))
const HallucinationAnalyticsPage = lazy(
  () => import('./pages/HallucinationAnalyticsPage')
)
const WhatsAppCostPage = lazy(() => import('./pages/WhatsAppCostPage'))
const CacheManagementPage = lazy(() => import('./pages/CacheManagementPage'))
const VerticalDemandPage = lazy(() => import('./pages/VerticalDemandPage'))
const AIDecisionsPage = lazy(() => import('./pages/AIDecisionsPage'))
const KnowledgeBasePage = lazy(() => import('./pages/KnowledgeBasePage'))
const ABExperimentsPage = lazy(() => import('./pages/ABExperimentsPage'))
const SelfHealingPage = lazy(() => import('./pages/SelfHealingPage'))
const AddonCatalogPage = lazy(() => import('./pages/AddonCatalogPage'))

function Loading() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
    </div>
  )
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuthStore()

  if (isLoading) {
    return <Loading />
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  return <>{children}</>
}

function App() {
  return (
    <Suspense fallback={<Loading />}>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route
          path="/*"
          element={
            <ProtectedRoute>
              <AdminLayout>
                <Routes>
                  <Route path="/" element={<DashboardPage />} />
                  <Route path="/workspaces" element={<WorkspacesPage />} />
                  <Route
                    path="/workspaces/:id"
                    element={<WorkspaceDetailPage />}
                  />
                  <Route path="/users" element={<UsersPage />} />
                  <Route path="/users/:id" element={<UserDetailPage />} />
                  <Route path="/channels" element={<ChannelsPage />} />
                  <Route path="/admins" element={<AdminsPage />} />
                  <Route path="/audit" element={<AuditLogPage />} />
                  <Route path="/settings" element={<SettingsPage />} />
                  <Route path="/billing" element={<BillingPage />} />
                  <Route
                    path="/platform-analytics"
                    element={<PlatformAnalyticsPage />}
                  />
                  <Route path="/compliance" element={<CompliancePage />} />
                  <Route
                    path="/workspaces/:id/branding"
                    element={<BrandingPage />}
                  />
                  <Route
                    path="/workspaces/:id/analytics"
                    element={<AnalyticsPage />}
                  />
                  <Route
                    path="/workspaces/:id/workflow"
                    element={<WorkflowRuleManagerPage />}
                  />
                  <Route
                    path="/workspaces/:id/webhooks"
                    element={<WebhookInspectionPage />}
                  />
                  <Route
                    path="/workspaces/:id/contacts"
                    element={<ContactBrowserPage />}
                  />
                  <Route path="/phone-pools" element={<PhonePoolsPage />} />
                  <Route path="/pricing" element={<PricingPlansPage />} />
                  <Route path="/addon-catalog" element={<AddonCatalogPage />} />
                  <Route path="/features" element={<FeatureManagementPage />} />
                  <Route path="/security" element={<SecurityDashboardPage />} />
                  <Route path="/ai-config" element={<AIConfigPage />} />
                  <Route path="/queues" element={<QueueManagementPage />} />
                  <Route
                    path="/ai-cost-analytics"
                    element={<AICostAnalyticsPage />}
                  />
                  <Route
                    path="/hallucination-analytics"
                    element={<HallucinationAnalyticsPage />}
                  />
                  <Route path="/whatsapp-cost" element={<WhatsAppCostPage />} />
                  <Route path="/cache" element={<CacheManagementPage />} />
                  <Route path="/verticals" element={<VerticalDemandPage />} />
                  <Route path="/ai-decisions" element={<AIDecisionsPage />} />
                  <Route
                    path="/knowledge-base"
                    element={<KnowledgeBasePage />}
                  />
                  <Route path="/experiments" element={<ABExperimentsPage />} />
                  <Route path="/self-healing" element={<SelfHealingPage />} />
                </Routes>
              </AdminLayout>
            </ProtectedRoute>
          }
        />
      </Routes>
    </Suspense>
  )
}

export default App
